import React, { useRef, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchSubQualificationTypes } from "../../../services/api/qualification";
import * as yup from "yup";
import { useFormik } from "formik";
import { addUserQualifications, deleteUserQualification } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { IMAGE_MAX_UPLOAD_SIZE } from "../../../utils/helpers";
const validationSchema = yup.object({
  file: yup
    .mixed()
    .required("File is required")
    .test(
      "fileSize",
      "File size is too large",
      (value) => value && value.size <= IMAGE_MAX_UPLOAD_SIZE,
    )
    .test(
      "fileType",
      "Unsupported file format (JPG, PNG, WEBP allowed)",
      (value) => value && ["image/jpeg", "image/png", "image/webp"].includes(value.type),
    ),
  qualificationSubTypeId: yup.string("").required("Choose the equipment"),
});
const AddEquipmentModal = ({
  open,
  close,
  qualificationSubTypeId,
  equipmentId,
  refetchUserQualificationsByType,
  equipments,
  targetUserId,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);
  const formik = useFormik({
    initialValues: {
      file: null,
      qualificationSubTypeId: qualificationSubTypeId,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleAddUserQualifications(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const {
    isPending: isDeleteUserQualificationApiLoading,
    error: isDeleteUserQualificationApiError,
    data: deletedUserQualificationsData,
    mutateAsync: deleteUserQualificationMutation,
  } = useMutation({
    mutationFn: (id) => deleteUserQualification(id, targetUserId),
    enabled: false,
  });

  const {
    isPending: isAddQualificationLoading,
    error: isAddQualificationError,
    data: addedQualificationsData,
    mutateAsync: addUserQualificationsMutation,
  } = useMutation({
    mutationFn: (payload) => addUserQualifications(payload, targetUserId),
    enabled: false,
  });

  const {
    isLoading: isSubQualificationsLoading,
    error: isSubQualificationsError,
    data: subQualificationsData,
  } = useQuery({
    queryKey: ["subQualifications", equipmentId],
    queryFn: () => fetchSubQualificationTypes(equipmentId),
    enabled: !!equipmentId,
  });

  const formattedSubQualifications = subQualificationsData?.data?.map((subQualification) => {
    return {
      label: subQualification?.name,
      value: subQualification?.id,
    };
  });

  const handleFileChange = (event) => {
    const file = event.target?.files[0];
    formik.setFieldValue("file", file);
  };

  // Remove the selected (not-yet-saved) file so the user can re-choose.
  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleAddUserQualifications = async (values) => {
    try {
      const formData = new FormData();
      formData.append("file", values.file);
      Object.keys(values).forEach((key) => {
        if (key !== "file") {
          formData.append(key, values[key]);
        }
      });
      await addUserQualificationsMutation(formData);
      dispatch(
        showToast({
          message: "Equipment added successfully",
          type: "success",
        }),
      );
      refetchUserQualificationsByType();
      close(false);
      formik.resetForm();
    } catch (error) {}
  };

  const handleEquipmentDelete = async () => {
    try {
      await deleteUserQualificationMutation(equipmentToDelete.id);
      await refetchUserQualificationsByType();
      dispatch(showToast({ message: "Equipment deleted successfully", type: "success" }));
      setEquipmentToDelete(null);
    } catch (error) {
      dispatch(showToast({ message: "Failed to delete equipment", type: "error" }));
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
        <h6 className="text-dark font-normal border-b pb-1">Add Equipments</h6>
        <div className="my-3">
          <p className="font-normal text-base mb-2">
            Add File <span className="font-300 text-sm">(JPG, PNG)</span>
          </p>
          <div className="w-full flex flex-wrap items-center gap-3">
            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="file"
              name="file"
              accept=".jpg,.jpeg,.png,.webp"
              onBlur={formik.handleBlur}
            />

            {/* File Display Section */}
            <div
              style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
              className="min-h-[40px] flex-1 min-w-[180px] py-[6.25px] rounded-md flex flex-wrap items-center"
            >
              {formik.values?.file ? (
                // Selected file rendered as a chip: clickable to preview + removable.
                <div className="ml-2 my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 max-w-full">
                  <a
                    href={URL.createObjectURL(formik.values.file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer text-blue-500 hover:underline font-300 truncate"
                  >
                    {formik.values?.file?.name}
                  </a>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800"
                    title="Remove file"
                  >
                    <i className="fa-solid fa-xmark text-xs"></i>
                  </button>
                </div>
              ) : (
                <p className="text-gray-400 py-1 ml-2">No file selected</p>
              )}
            </div>

            {/* Choose File Button */}
            <BaseButton
              variant="outlined"
              color="primary"
              className="py-[7px] px-5 shrink-0 whitespace-nowrap"
              onClick={handleButtonClick}
            >
              Choose File
            </BaseButton>
          </div>
          {formik.touched.file && formik.errors.file && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.file}</p>
          )}
        </div>
        <BaseAutoSelect
          isDefaultField={false}
          placeholder="Select Equipment"
          className="mt-6"
          options={formattedSubQualifications}
          multiple={false}
          id="qualificationSubTypeId"
          name="qualificationSubTypeId"
          value={formik.values.qualificationSubTypeId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.qualificationSubTypeId && Boolean(formik.errors.qualificationSubTypeId)
          }
          helperText={formik.touched.qualificationSubTypeId && formik.errors.qualificationSubTypeId}
        />
      </div>
      <div className="flex flex-wrap gap-4 px-12">
        {equipments?.map((equipment) => {
          const fileUrl = equipment?.url || equipment?.digitalAsset?.url;
          const fileName = equipment?.fileName || equipment?.digitalAsset?.fileName;
          return (
            <div key={equipment.id} className="flex items-center px-4 py-1 bg-light rounded-sm">
              {fileUrl ? (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={fileName ? `View file: ${fileName}` : "View file"}
                  className="cursor-pointer hover:text-blue-500 hover:underline"
                >
                  {equipment?.qualificationSubType}
                </a>
              ) : (
                <p>{equipment?.qualificationSubType}</p>
              )}
              <i
                className="fa-solid fa-xmark ml-2 cursor-pointer hover:text-blue-500"
                onClick={() => setEquipmentToDelete(equipment)}
              ></i>
            </div>
          );
        })}
      </div>

      <div
        className="d-flex content-center bg-white py-6 mt-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isAddQualificationLoading}
          loaderSize={"22px"}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={formik.handleSubmit}
          // Disabled by default; enabled only when there are valid unsaved changes.
          disabled={!formik.dirty || !formik.isValid || isAddQualificationLoading}
        >
          Save Changes
        </BaseButton>
      </div>

      {equipmentToDelete && (
        <BaseConfirmModal
          open={!!equipmentToDelete}
          onClose={() => setEquipmentToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Equipment"
          description="Are you sure you want to delete this equipment? This action cannot be undone."
          isLoading={isDeleteUserQualificationApiLoading}
          onConfirm={handleEquipmentDelete}
        />
      )}
    </BaseModal>
  );
};

export default AddEquipmentModal;
